import { of as observableOf, Subject, Observable } from 'rxjs'
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http'
import { DiffComponent } from '@exchange/ui/shared/controls'
import { EpisodeLinkRendererComponent } from '../episode-link.renderer.component'
import {
  ClaimLineExportODataService,
  ClaimLineServiceAdjustmentODataService,
  ClaimLineViewODataService,
  ClaimLineX12ODataService,
  ClaimAdjustedReceivedDatesViewODataService,
  IClaimLineAction,
  IClaimLineExport,
  IClaimLineView,
  IClaimLineX12,
  IClaimAdjustedReceivedDate,
  IClaimAdjustedReceivedDatesView,
  IClaimDelete
} from '@exchange/domain/.'
import { SessionService, UtilService } from '@exchange/services/.'
import { AuditDetailComponent } from '../audit-detail/audit-detail.component'
import { X12Codes } from '@exchange/domain/lookups'
import moment from 'moment-timezone'
import _ from 'lodash'
import { environment } from '@exchange/environment'
import { GridQuickFilterServiceDirective, IQuickFilter } from '@exchange/domain/.'
import { IFilterOption } from '@exchange/domain/gridQuickFilter'
import { map, mergeMap, takeUntil, tap } from 'rxjs/operators'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { ToastrService } from 'ngx-toastr'
import {
  ConfirmModalComponent,
  GridSize,
  GridWrapperComponent,
  NotifyModalComponent
} from 'common-components'
import { ClaimUpdateService } from '@exchange/app/services/claimUpdateService'
import { ProcessingProgressService, IProcessingProgressStep } from '@exchange/app/services/processingProgressService'
import { Table } from 'primeng/table'

@Component({
  selector: 'app-claims-list',
  templateUrl: './claims-list.component.html',
  styleUrls: ['./claims-list.component.scss'],
  standalone: false
})
export class ClaimsListComponent implements OnInit, OnDestroy {
  GridSize = GridSize
  model: IClaimLineView = null
  isProgressViewShown = false
  progressSteps: IProcessingProgressStep[] = []
  progressLastRun: string = null
  expandedRows: { [key: string]: boolean } = {}
  isExportViewShown = false
  isX12ViewShown = false
  isAuditView = false
  isCodesView = false
  hasSelection = false
  differences = 0
  currentDiff = 0
  claimActions = null
  user = null
  userTimezone: string = moment.tz.guess()
  summaryConfigComparisonSubscription = null
  confirmActionModalTpl = null
  @ViewChild('progressTable') progressTable: Table
  @ViewChild('grid', { static: true }) grid: GridWrapperComponent
  @ViewChild('gridExport', { static: true }) gridExport: GridWrapperComponent
  @ViewChild('diff', { static: true }) diff: DiffComponent
  @ViewChild('auditDetail', { static: true }) auditDetail: AuditDetailComponent
  private ngUnsubscribe: Subject<boolean> = new Subject()
  detailViewColumns: any[] = []

  quickFilters: IFilterOption[] = [
    {
      displayName: 'CHI Claim #',
      fieldName: 'ClaimId',
      type: 'number',
      operation: 'contains',
      default: true
    },
    {
      displayName: 'Provider Claim #',
      fieldName: 'Identifier',
      type: 'string',
      operation: 'contains'
    },
    {
      displayName: 'Provider Name',
      fieldName: 'ProviderName',
      type: 'string',
      operation: 'contains'
    },
    {
      displayName: 'Provider NPI',
      fieldName: 'BillingProviderNpi',
      type: 'number',
      operation: 'contains'
    },
    {
      displayName: 'Check #',
      fieldName: 'CheckNumber',
      type: 'number',
      operation: 'contains'
    },
    {
      displayName: 'Patient',
      fieldName: 'PatientName',
      type: 'string',
      operation: 'contains'
    }
  ]

  constructor(
    private odata: ClaimLineViewODataService,
    private odataSeviceAdjustment: ClaimLineServiceAdjustmentODataService,
    private odataExport: ClaimLineExportODataService,
    private odataX12: ClaimLineX12ODataService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private session: SessionService,
    private gridQuickFilterServiceDirective: GridQuickFilterServiceDirective,
    private modalService: BsModalService,
    public toastr: ToastrService,
    private util: UtilService,
    private processingProgress: ProcessingProgressService
  ) {
    this.gridQuickFilterServiceDirective.search.subscribe(this.quickFilter.bind(this))
    this.gridQuickFilterServiceDirective.resetFilters.subscribe(this.quickFilterReset.bind(this))
  }

  ngOnInit() {
    this.initializeGrid()
    this.initializeExportGrid()

    this.session
      .getCurrentUser()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(user => {
        this.user = user
        this.userTimezone = (user as any).timezone || this.userTimezone
        if (this.grid.gridOptions.context) {
          this.grid.gridOptions.context.userTimezone = this.userTimezone
        }
      })
  }

  get formattedLastRun(): string {
    if (!this.progressLastRun) return ''
    return moment.utc(this.progressLastRun).tz(this.userTimezone).format('M/D/YY h:mm:ss a')
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(false)
    this.ngUnsubscribe.complete()
  }

  gridSelectionChanged() {
    const rows = this.grid.api.getSelectedRows()
    this.hasSelection = !!rows.length
    this.model = null

    if (this.hasSelection) {
      this.model = rows[0] as IClaimLineView
      this.getAvailableActions().subscribe()
      if (this.model.ClaimType === 'Professional') {
        this.setDetailViewProfessional()
      } else if (this.model.ClaimType === 'Institutional') {
        this.setDetailViewInstitutional()
      }
    }
    this.closePanels()
    this.grid.api.redrawRows()
  }

  getAvailableActions(): Observable<object> {
    const url = `${environment.tmapiroot}/odata/ClaimsView/?$filter=Id eq ${this.model.Id}&$expand=AvailableActions`

    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json'
    })

    return this.http
      .get(url, { headers })
      .pipe(tap(data => (this.model.AvailableActions = _.get(data, 'value[0].AvailableActions'))))
  }

  toggleProcessingProgress(): void {
    this.isProgressViewShown = !this.isProgressViewShown
    if (this.isProgressViewShown) {
      if (this.progressSteps.length === 0) {
      this.processingProgress
        .getProgressSteps(this.model.ClaimId, this.model.ServiceId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(steps => {
          this.progressSteps = steps
          this.progressLastRun = steps.length
            ? steps.reduce((latest, s) => s.timestamp > latest ? s.timestamp : latest, steps[0].timestamp)
            : null
          this.expandedRows = {}
          setTimeout(() => {
            const el = document.querySelector('#claim-progress-grid')
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }
          }, 1)
        })
      } else {
        setTimeout(() => {
            const el = document.querySelector('#claim-progress-grid')
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }
          }, 1)
        this.processingProgress
        .getProgressSteps(this.model.ClaimId, this.model.ServiceId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(steps => {
          this.progressSteps = steps
          this.progressLastRun = steps.length
            ? steps.reduce((latest, s) => s.timestamp > latest ? s.timestamp : latest, steps[0].timestamp)
            : null
        })
      }
    }
  }

  toggleRowIfNotSelecting(step: IProcessingProgressStep, event: MouseEvent): void {
    const selection = window.getSelection()
    if (selection && selection.toString().length > 0) {
      return
    }
    this.progressTable.toggleRow(step, event)
    if (this.expandedRows[step.stepName]) {
      setTimeout(() => {
        const clickedRow = (event.target as HTMLElement).closest('tr')
        if (clickedRow?.nextElementSibling) {
          clickedRow.nextElementSibling.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Passed': return 'green'
      case 'Failed': return 'red'
      case 'Skipped': return 'gray'
      default: return 'inherit'
    }
  }

  toggleEncounterConfigComparison(): void {
    this.isExportViewShown = !this.isExportViewShown
    if (this.isExportViewShown) {
      this.gridExport.defaultFilter = `EdiFileRevisionId eq ${this.model.FileId}`
      this.gridExport.odata = this.odataExport
      if (!this.gridExport.data || this.gridExport.data[0].ServiceId !== this.model.ServiceId) {
        this.gridExport.getData()
      }
      this.gridExport.api.sizeColumnsToFit()
      this.scrollElementIntoView('#claim-export-grid')
    }
  }

  showSummaryConfigComparison(): void {
    if (this.isX12ViewShown) {
      this.closeX12View()
      return
    }
    if (
      this.summaryConfigComparisonSubscription &&
      !this.summaryConfigComparisonSubscription.closed
    ) {
      this.summaryConfigComparisonSubscription.complete()
    }
    this.grid.api.redrawRows()
    this.differences = 0
    this.currentDiff = 0
    const action = `?$filter=LeftEdiFileRevisionId eq ${this.model.FileId}`
    this.summaryConfigComparisonSubscription = this.odataX12
      .restGet(action)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(response => {
        const x12 = _.get(response[0], 'value[0]') as IClaimLineX12
        if (!x12) {
          this.toastr.error('Summary config comparison was not found.')
          return
        }
        this.isX12ViewShown = true

        const left = x12.Left.replace(/~/g, '~\r\n')
        const right = x12.Right.replace(/~/g, '~\r\n')

        this.differences = this.diff.compare(left, right)
        this.scrollElementIntoView('#claim-x12-comparison')
      }, this.showError.bind(this))
  }

  showAuditDetail(): void {
    this.isAuditView = !this.isAuditView
    this.scrollElementIntoView('#claim-auditDetail-grid')
  }

  showCodesView(): void {
    this.isCodesView = !this.isCodesView
    this.scrollElementIntoView('#codes')
  }

  refresh(): void {
    this.model = null
    this.hasSelection = false
    this.closePanels()
    this.grid.getData()
  }

  closePanels(): void {
    this.isProgressViewShown = false
    this.progressSteps = []
    this.expandedRows = {}

    this.isExportViewShown = false
    this.gridExport.data = null
    this.gridExport.clearFilter()
    this.gridExport.clearSort()

    this.closeX12View()
    this.closeAuditView()
    this.closeCodesView()
  }

  closeX12View(): void {
    this.isX12ViewShown = false
  }

  closeAuditView(): void {
    this.isAuditView = false
  }

  closeCodesView(): void {
    this.isCodesView = false
  }

  gotoDiff(offset: number): void {
    let id = `#${this.diff.resultid}-${this.currentDiff}`
    let elem: any = document.querySelector(id)
    if (elem) {
      elem.style.fontWeight = 'normal'
    }
    this.currentDiff += offset
    if (this.currentDiff < 0) {
      this.currentDiff = 1
    }
    if (this.currentDiff > this.differences) {
      this.currentDiff = this.differences
    }
    id = `#${this.diff.resultid}-${this.currentDiff}`
    elem = document.querySelector(id)
    if (elem) {
      elem.style.fontWeight = 'bold'
      elem.scrollIntoView({ block: 'nearest', inline: 'center' })
    }
  }

  performAction(claimAction: IClaimLineAction): void {
    const url = `${environment.tmapiroot}/ClaimActions/${claimAction.Uri}`
    const method = `${claimAction.HttpMethod.toLowerCase()}`
    const reqOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json'
      }),
      body: ''
    }

    const makeRequest = this.http.request(method, url, reqOptions)

    // show the toast message "received" before we make the request to give the user immediate feedback, then show any error messages if the request fails
    this.validateAction(claimAction)
      .pipe(
        mergeMap(isValidAction => {
          return isValidAction ? makeRequest : observableOf(null)
        })
      )
      .subscribe(() => {
        this.toastr.success(`${claimAction.ActionName} received.`)
      })
  }

  validateAction(claimAction: IClaimLineAction): Observable<boolean> {
    if (claimAction && claimAction.ActionName) {
      if (claimAction.ActionName === 'Reject') {
        return this.claimHasAdjustmentReason(this.model).pipe(
          mergeMap(isValid =>
            isValid ? this.showConfirmModal(claimAction) : this.showRejectInvalidModal()
          )
        )
      } else {
        return this.showConfirmModal(claimAction)
      }
    }
  }

  claimHasAdjustmentReason(claim: IClaimLineView): Observable<boolean> {
    const filter = `?$filter=ClaimId eq ${claim.ClaimId} and ServiceId eq ${claim.ServiceId}`

    return this.odataSeviceAdjustment
      .restGet(filter)
      .pipe(map(resp => resp && Array.isArray(resp.value) && resp.value.length))
  }

  showRejectInvalidModal(): Observable<any> {
    return this.modalService
      .show(NotifyModalComponent, {
        initialState: {
          title: 'Claim Rejection Not Valid',
          message: 'Please enter a claim adjustment code to reject this claim line.'
        }
      })
      .content.onClose.pipe(map(() => false))
  }

  showConfirmModal(claimAction: IClaimLineAction): Observable<boolean> {
    return this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `${claimAction.ActionName} Claim`,
          message: `Are you sure you want to ${claimAction.ActionName.toLowerCase()} this claim?`
        }
      })
      .content.onClose.pipe(map(userConfirmation => userConfirmation))
  }

  showError(response: HttpErrorResponse): void {
    const msg: string = this.util.getErrorString(response)

    this.toastr.error(msg)
  }

  configFieldChanged(e): void {
    this.odataExport
      .put(e.data, e.data.Id)
      .exec()
      .subscribe(
        () => {
          this.toastr.success('Updated successfully')
          this.auditDetail.Refresh()
          this.gridExport.getData()
          this.updateAuditDetail(e.data)
        },
        () => {
          this.toastr.error('Error when updating config item')
          this.auditDetail.Refresh()
          this.gridExport.getData()
        }
      )
  }

  updateAuditDetail(data: IClaimLineExport) {
    const field = (data.ParentId ? data.ParentId + '/' : '') + data.SegmentId + '/' + data.ElementId

    this.auditDetail.auditInfo.push({
      FieldName: field,
      OldValue: data.OldValue,
      NewValue: data.NewValue,
      Date: moment().format(this.auditDetail.dateFormat),
      Username: this.user.FirstName + ' ' + this.user.LastName
    })

    this.auditDetail.gridAuditInfo.data = _.cloneDeep(this.auditDetail.auditInfo)
  }

  quickFilter(item: IQuickFilter) {
    this.grid.defaultFilter = item.odataFilter
    this.grid.getData()
  }

  quickFilterReset() {
    this.grid.defaultFilter = ''
    this.grid.getData()
  }

  showClaimAdjustedReceivedDateModal() {
    this.modalService.show(ClaimAdjustedRecivedDateModalComponent, {
      initialState: {
        claimModel: this.model,
        onSuccess: () => this.grid.getData()
      }
    })
  }
  showClaimDeleteModal() {
    this.modalService.show(ClaimDeleteModalComponent, {
      initialState: {
        claimModel: this.model,
        onSuccess: () => this.grid.getData()
      }
    })
  }

  private scrollElementIntoView(selector): void {
    const el = document.querySelector(selector)
    const go = () => {
      el.scrollIntoView({ behavior: 'smooth' })
    }

    if (el) {
      setTimeout(go, 100)
    }
  }

  private setDetailViewInstitutional() {
    this.detailViewColumns = [
      {
        headerName: 'File ID',
        field: 'FileId'
      },
      {
        headerName: 'CHI Clm ID',
        field: 'ClaimId'
      },
      {
        headerName: 'Provider Clm ID',
        field: 'Identifier'
      },
      {
        headerName: 'Line',
        field: 'ServiceLineId'
      },
      {
        headerName: 'Patient Episode',
        field: 'PatientEpisodeId'
      },
      {
        headerName: 'Patient',
        field: 'PatientName'
      },
      {
        headerName: 'Auth Number',
        field: 'AuthorizationNumber'
      },
      {
        headerName: 'Plan',
        field: 'PlanName'
      },
      {
        headerName: 'Subscriber ID',
        field: 'SubscriberId'
      },
      {
        headerName: 'Subscriber Name',
        field: 'SubscriberName'
      },
      {
        headerName: 'Member ID',
        field: 'PatientId'
      },
      {
        headerName: 'Member Name',
        field: 'PatientName'
      },
      {
        headerName: 'PX Code',
        field: 'ProcedureCode'
      },
      {
        headerName: 'Modifier(s)',
        field: 'Modifiers'
      },
      {
        headerName: 'DX Codes',
        field: 'DiagnosisCodes'
      },
      {
        headerName: 'Revenue Code',
        field: 'RevenueCode'
      },
      {
        headerName: 'Date of Service',
        cellRenderer: model => {
          const dos = model.DateOfService || model.StartDate

          return moment(dos)
            .utc()
            .format('MM/DD/YYYY')
        }
      },
      {
        headerName: 'Units',
        field: 'Units'
      },
      {
        headerName: 'Processing Status',
        field: 'Status'
      },
      {
        headerName: 'Frequency',
        field: 'FrequencyTypeCode',
        toolTip: 'FrequencyTypeDescription'
      },
      {
        headerName: 'Claim Type',
        field: 'ClaimType'
      },
      {
        headerName: 'Statement Date',
        cellRenderer: model => {
          const formattedStart = moment(model.StartDate)
            .utc()
            .format('MM/DD/YYYY')
          const formattedThrough = moment(model.ThroughDate)
            .utc()
            .format('MM/DD/YYYY')

          return formattedThrough && formattedThrough !== formattedStart
            ? `${formattedStart} - ${formattedThrough}`
            : formattedStart
        }
      },
      {
        headerName: 'Days Since Received',
        field: 'DaysSinceReceived'
      },
      {
        headerName: 'Provider',
        field: 'ProviderName'
      },
      {
        headerName: 'Billing NPI',
        field: 'BillingProviderNpi'
      },
      {
        headerName: 'Billed Amount',
        field: 'BilledAmount'
      },
      {
        headerName: 'Validation Status',
        field: 'ValidationStatus'
      },
      {
        headerName: 'Days In Status',
        field: 'DaysInStatus'
      },
      {
        headerName: 'Phase',
        field: 'Phase'
      },
      {
        headerName: 'Original File',
        field: 'OriginalFileName'
      },
      {
        headerName: 'Current File',
        field: 'CurrentFileName'
      },
      {
        headerName: 'Place Of Service',
        field: 'PlaceOfService'
      },
      {
        headerName: 'Line of Business',
        field: 'LineOfBusiness'
      },
      {
        headerName: 'Adjudication Type',
        field: 'AdjudicationType'
      },
      {
        headerName: 'Auto Adjudicated',
        field: 'AutoAdjudicated',
        cellRenderer: model => {
          return model.AutoAdjudicated ? 'Yes' : 'No'
        }
      }
    ]
  }

  private setDetailViewProfessional() {
    this.detailViewColumns = [
      {
        headerName: 'File ID',
        field: 'FileId'
      },
      {
        headerName: 'CHI Clm ID',
        field: 'ClaimId'
      },
      {
        headerName: 'Provider Clm ID',
        field: 'Identifier'
      },
      {
        headerName: 'Line',
        field: 'ServiceLineId'
      },
      {
        headerName: 'Patient Episode',
        field: 'PatientEpisodeId'
      },
      {
        headerName: 'Patient',
        field: 'PatientName'
      },
      {
        headerName: 'Auth Number',
        field: 'AuthorizationNumber'
      },
      {
        headerName: 'Plan',
        field: 'PlanName'
      },
      {
        headerName: 'Subscriber ID',
        field: 'SubscriberId'
      },
      {
        headerName: 'Subscriber Name',
        field: 'SubscriberName'
      },
      {
        headerName: 'Member ID',
        field: 'PatientId'
      },
      {
        headerName: 'Member Name',
        field: 'PatientName'
      },
      {
        headerName: 'PX Code',
        field: 'ProcedureCode'
      },
      {
        headerName: 'Modifier(s)',
        field: 'Modifiers'
      },
      {
        headerName: 'DX Codes',
        field: 'DiagnosisCodes'
      },
      {
        headerName: 'Claim Type',
        field: 'ClaimType'
      },
      {
        headerName: 'Units',
        field: 'Units'
      },
      {
        headerName: 'Date of Service',
        field: 'DateOfService',
        cellRenderer: ({ DateOfService }) => {
          return moment(DateOfService)
            .utc()
            .format('MM/DD/YYYY')
        }
      },
      {
        headerName: 'Processing Status',
        field: 'Status'
      },
      {
        headerName: 'Frequency',
        field: 'FrequencyTypeCode',
        toolTip: 'FrequencyTypeDescription'
      },
      {
        headerName: 'Days Since Received',
        field: 'DaysSinceReceived'
      },
      {
        headerName: 'Provider',
        field: 'ProviderName'
      },
      {
        headerName: 'Billing NPI',
        field: 'BillingProviderNpi'
      },
      {
        headerName: 'Billed Amount',
        field: 'BilledAmount'
      },
      {
        headerName: 'Validation Status',
        field: 'ValidationStatus'
      },
      {
        headerName: 'Days In Status',
        field: 'DaysInStatus'
      },
      {
        headerName: 'Phase',
        field: 'Phase'
      },
      {
        headerName: 'Original File',
        field: 'OriginalFileName'
      },
      {
        headerName: 'Current File',
        field: 'CurrentFileName'
      },
      {
        headerName: 'Place Of Service',
        field: 'PlaceOfService'
      },
      {
        headerName: 'Line of Business',
        field: 'LineOfBusiness'
      },
      {
        headerName: 'Adjudication Type',
        field: 'AdjudicationType'
      },
      {
        headerName: 'Auto Adjudicated',
        field: 'AutoAdjudicated',
        cellRenderer: model => {
          return model.AutoAdjudicated ? 'Yes' : 'No'
        }
      }
    ]
  }

  private initializeGrid(): void {
    this.grid.gridOptions.context = {
      canEdit: false,
      canDelete: false
    }

    this.grid.gridOptions.rowSelection = { mode: 'singleRow' }

    this.grid.gridOptions.columnDefs = [
      {
        headerName: 'Processing Status',
        field: 'Status',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 130,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Days Since Received',
        field: 'DaysSinceReceived',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100
      },
      {
        headerName: 'Validation Status',
        field: 'ValidationStatus',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 130,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'CHI Clm ID',
        field: 'ClaimId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Provider Clm ID',
        field: 'Identifier',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Ln',
        field: 'ServiceLineId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 50
      },
      {
        headerName: 'Provider',
        field: 'ProviderName',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Patient',
        field: 'PatientName',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 120,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Patient Episode',
        field: 'PatientEpisodeId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: EpisodeLinkRendererComponent
      },
      {
        headerName: 'DOS',
        field: 'DateOfService',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: ({ data: { DateOfService, StartDate } }) => {
          const dos = DateOfService || StartDate

          return moment(dos)
            .utc()
            .format('MM/DD/YYYY')
        }
      },
      {
        headerName: 'DX Codes',
        field: 'DiagnosisCodes',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'PX Code',
        field: 'ProcedureCode',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 80,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Billed Amount',
        field: 'BilledAmount',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 130,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Claim Type',
        field: 'ClaimType',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Days In Status',
        field: 'DaysInStatus',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 130
      }
    ]

    this.grid.gridOptions.getRowStyle = (par: any) => {
      if (!par || !par.data || !this.model) {
        return null
      }
      if (
        par.data['ClaimId'] === this.model.ClaimId &&
        par.data['ServiceLineId'] === this.model.ServiceLineId
      ) {
        return { color: 'black', 'background-color': 'LightBlue' }
      } else if (par.data['ClaimId'] === this.model.ClaimId) {
        return { color: 'black', 'background-color': 'Plum' }
      }
      return null
    }

    this.grid.gridSize = GridSize.small
    this.grid.pageSize = 100 // this has to be set after gridSize
    this.grid.gridOptions.onSelectionChanged = this.gridSelectionChanged.bind(this)
    this.grid.defaultOrder = [{ colId: 'ClaimId', sort: 'desc' }]
    this.grid.odata = this.odata
  }

  private isCellEditable(data) {
    if (!this.model) {
      return false
    }
    const allowEditStatus = ['Validation Failed', 'Under Review', 'Pending Release']
    const lockedCells = [
      { ParentId: '', SegmentId: 'ISA', ElementId: 9 },
      { ParentId: '', SegmentId: 'ISA', ElementId: 10 },
      { ParentId: '*', SegmentId: 'NM1', ElementId: 1 }
    ]

    const dat = data.node.data as IClaimLineExport
    const isLocked = lockedCells.find(
      x =>
        (x.ParentId === '*' || x.ParentId === dat.ParentId) &&
        x.SegmentId === dat.SegmentId &&
        x.ElementId === dat.ElementId
    )

    return allowEditStatus.indexOf(this.model.Status) > -1 && !isLocked
  }

  private configCellStyle(data) {
    return this.isCellEditable(data) ? { 'background-color': 'lightBlue' } : {}
  }

  private initializeExportGrid(): void {
    // Encounter Config Comparison
    this.gridExport.gridOptions.columnDefs = [
      {
        headerName: 'Configuration Field',
        field: 'FieldName',
        // TODO: Enable filters once Config Field runs server-side
        // filter: 'agTextColumnFilter',
        // filterParams: {  newRowsAction: 'keep' },
        menuTabs: [],
        sortable: false,
        width: 400,
        cellRenderer: params => {
          const data = params.data as IClaimLineExport
          const match = X12Codes.Find('837', data.SegmentId, data.ElementId, data.NewValue)
          if (match) {
            return `<span title="${match.ToolTip}">${match.FriendlyName}</span>`
          }

          return ''
        }
      },
      {
        headerName: '837 In',
        field: 'OldValue',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 150,
        cellRenderer: (params: any) => {
          const def = X12Codes.Find(
            '837',
            params.data.SegmentId,
            params.data.ElementId,
            params.data.OldValue
          )
          const formatted = X12Codes.FormatValue(params.data.OldValue, def)
          return `<span title="${formatted}">${formatted}</span>`
        }
      },
      {
        headerName: '837 Out',
        field: 'NewValue',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 150,
        editable: this.isCellEditable.bind(this),
        cellRenderer: (params: any) => {
          const def = X12Codes.Find(
            '837',
            params.data.SegmentId,
            params.data.ElementId,
            params.data.NewValue
          )
          const formatted = X12Codes.FormatValue(params.data.NewValue, def)
          return `<span title="${formatted}">${formatted}</span>`
        },
        cellStyle: this.configCellStyle.bind(this)
      },
      {
        headerName: 'Parent Segment',
        field: 'ParentId',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Segment',
        field: 'SegmentId',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'ElementId',
        field: 'ElementId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      }
    ]
    this.gridExport.pageSize = 100
  }
}

@Component({
  templateUrl: './claim-adjusted-received-date-modal.component.html',
  standalone: false
})
export class ClaimAdjustedRecivedDateModalComponent {
  claimModel: IClaimLineView
  onSuccess: () => void

  claimSearchTerm = ''
  claimSearchResult = ''
  claimAdjustedReason = ''
  originalClaim: IClaimAdjustedReceivedDatesView = null

  constructor(
    public modalRef: BsModalRef,
    private odataClaimAdjustedReceivedDatesView: ClaimAdjustedReceivedDatesViewODataService,
    private claimUpateService: ClaimUpdateService,
    private toastr: ToastrService
  ) {}

  searchClaim() {
    if (isNaN(parseInt(this.claimSearchTerm, 10))) {
      this.originalClaim = null
      this.claimSearchResult = '<b>Invalid search.</b>'
      return
    }
    if (this.claimSearchTerm === this.claimModel.ClaimId.toString()) {
      this.originalClaim = null
      this.claimSearchResult = '<b>Cannot update claim with itself.</b>'
      return
    }
    this.odataClaimAdjustedReceivedDatesView
      .query()
      .filter(`ClaimId eq ${this.claimSearchTerm}`)
      .exec()
      .subscribe(response => {
        if (response.data.length === 0) {
          this.originalClaim = null
          this.claimSearchResult = '<b>No results found.</b>'
          return
        }
        this.originalClaim = response.data[0]
        if (this.originalClaim.ClaimType !== this.claimModel.ClaimType) {
          this.claimSearchResult = '<b>Different claim type, choose another claim.</b>'
          this.originalClaim = null
        } else {
          this.claimSearchResult = `
          <b>CHI Clm ID:</b> ${this.originalClaim.ClaimId}<br/>
          <b>Patient:</b> ${this.originalClaim.PatientName}<br/>
          <b>Days Since Received:</b> ${this.originalClaim.DaysSinceReceived}
          ${
            this.originalClaim.IsClaimDeleted
              ? `</br><b><span class='text-danger'>Claim has been deleted</span></b>`
              : ''
          }
        `
          this.claimAdjustedReason = ''
        }
      })
  }

  claimAdjustedReceivedDateSubmit(event: any) {
    if (this.claimAdjustedReason === '') {
      this.toastr.error('Please add a reason for the change.')
      return
    }

    event.target.disabled = true

    this.claimUpateService
      .postClaimAdjustedDate({
        ClaimId: this.claimModel.ClaimId,
        OriginalClaimId: this.originalClaim.ClaimId,
        ChangeReason: this.claimAdjustedReason
      } as IClaimAdjustedReceivedDate)
      .subscribe(
        _response => {
          this.toastr.success('Claim Updated')
          this.modalRef.hide()
          event.target.disabled = false
          this.onSuccess()
        },
        _error => {
          this.modalRef.hide()
          this.toastr.error('Error when updating claim')
          event.target.disabled = false
        }
      )
  }
}

@Component({
  templateUrl: './claim-delete-modal.component.html',
  standalone: false
})
export class ClaimDeleteModalComponent implements OnInit {
  claimModel: IClaimLineView
  onSuccess: () => void

  deleteReason = ''
  constructor(
    public modalRef: BsModalRef,
    private claimUpateService: ClaimUpdateService,
    private toastr: ToastrService
  ) {}
  ngOnInit(): void {
    this.deleteReason = ''
  }

  claimDeleteSubmit(event: any) {
    event.target.disabled = true

    this.claimUpateService
      .postClaimDelete({
        ClaimId: this.claimModel.ClaimId,
        Reason: this.deleteReason
      } as IClaimDelete)
      .subscribe(
        _response => {
          this.toastr.success('Claim Deleted')
          this.modalRef.hide()
          event.target.disabled = false
          this.onSuccess()
        },
        _error => {
          this.modalRef.hide()
          this.toastr.error('Error when deleting claim')
          event.target.disabled = false
        }
      )
  }
}
